import './App.css';
import '@fontsource/quicksand/400.css';
import '@fontsource/quicksand/500.css';
import '@fontsource/quicksand/700.css';
import { useEffect, useMemo, useState } from 'react';

const DAYS = [
  { id: '2026-09-11', weekday: 'Friday', day: '11 Sep', label: 'Friday 11 Sep', start: '10:30', end: '18:00' },
  { id: '2026-09-12', weekday: 'Saturday', day: '12 Sep', label: 'Saturday 12 Sep', start: '09:30', end: '18:00' },
  { id: '2026-09-13', weekday: 'Sunday', day: '13 Sep', label: 'Sunday 13 Sep', start: '09:30', end: '18:00' },
  { id: '2026-09-14', weekday: 'Monday', day: '14 Sep', label: 'Monday 14 Sep', start: '09:30', end: '16:00' },
];

const DASHBOARD_DAYS = [
  { id: 'all', label: 'All days' },
  ...DAYS.map((day) => ({ id: day.id, label: day.label })),
];

const SLOT_MINUTES = 30;
const EVENT_LOCATION = 'RAI Amsterdam · Hall 1 — 1.C37e';
const EVENT_LOCATION_URL = 'https://directory.ibc.org/8_0/floorplan/index.cfm?hallID=A&level=1&selectedBooth=booth%7E1.C37e';
const TIME_ZONE = 'Europe/Amsterdam';
const API_BASE = process.env.REACT_APP_API_BASE || '';

function timeToMinutes(time) {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function minutesToTime(minutes) {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function makeSlots(day) {
  const slots = [];
  const first = timeToMinutes(day.start);
  const last = timeToMinutes(day.end) - SLOT_MINUTES;

  for (let cursor = first; cursor <= last; cursor += SLOT_MINUTES) {
    slots.push(minutesToTime(cursor));
  }

  return slots;
}

function addMinutes(time, minutesToAdd) {
  return minutesToTime(timeToMinutes(time) + minutesToAdd);
}

function formatSlot(day, time) {
  return `${day.label}, ${time}-${addMinutes(time, SLOT_MINUTES)}`;
}

function toCompactDate(date) {
  return date.replaceAll('-', '');
}

function toCompactTime(time) {
  return time.replace(':', '') + '00';
}

function makeCalendarUrl(day, time, form) {
  const end = addMinutes(time, SLOT_MINUTES);
  const title = `Kineton <> ${form.company || form.fullName} @ IBC 2026`;
  const details = `Meeting with ${form.fullName}${form.phone ? ` · ${form.phone}` : ''}`;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${toCompactDate(day.id)}T${toCompactTime(time)}/${toCompactDate(day.id)}T${toCompactTime(end)}`,
    ctz: TIME_ZONE,
    location: EVENT_LOCATION,
    details,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function downloadIcs(day, time, form) {
  const end = addMinutes(time, SLOT_MINUTES);
  const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const summary = `Kineton <> ${form.company || form.fullName} @ IBC 2026`;
  const description = `Meeting with ${form.fullName}\\nCompany: ${form.company}\\nEmail: ${form.email}\\nPhone: ${form.phone}`;
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Kineton//IBC Booking//EN',
    'BEGIN:VEVENT',
    `UID:${day.id}-${time}-${form.email}@kineton-ibc`,
    `DTSTAMP:${now}`,
    `DTSTART;TZID=${TIME_ZONE}:${toCompactDate(day.id)}T${toCompactTime(time)}`,
    `DTEND;TZID=${TIME_ZONE}:${toCompactDate(day.id)}T${toCompactTime(end)}`,
    `SUMMARY:${summary}`,
    `LOCATION:${EVENT_LOCATION}`,
    `DESCRIPTION:${description}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `kineton-ibc-${day.id}-${time.replace(':', '')}.ics`;
  link.click();
  URL.revokeObjectURL(url);
}

function Stepper({ step }) {
  const items = ['Pick a time', 'Your details', 'Done'];

  return (
    <nav className="stepper" aria-label="Booking progress">
      {items.map((item, index) => {
        const number = index + 1;
        return (
          <div className="stepperItem" key={item}>
            <span className={number <= step ? 'stepBubble active' : 'stepBubble'}>{number}</span>
            <span className={number === step ? 'stepLabel current' : 'stepLabel'}>{item}</span>
          </div>
        );
      })}
    </nav>
  );
}

function BrandPanel() {
  return (
    <aside className="brandPanel">
      <div>
        <div className="brandMark" aria-label="Kineton">k<span /></div>
        <p className="eyebrow">IBC 2026 · Amsterdam</p>
        <h1>Come and say hi at our stand.</h1>
        <p className="intro">Pick a half hour that suits you and we'll keep it free — coffee, a demo and a proper chat.</p>
      </div>
      <div className="eventMeta" aria-label="Event details">
        <div><span>When</span><strong>11-14 September 2026</strong></div>
        <div>
          <span>Where</span>
          <strong>
            <a className="locationLink" href={EVENT_LOCATION_URL} rel="noreferrer" target="_blank">
              {EVENT_LOCATION}<span aria-hidden="true">↗</span>
            </a>
          </strong>
        </div>
        <div><span>Slot</span><strong>30 minutes</strong></div>
      </div>
      <span className="brandShape" aria-hidden="true" />
    </aside>
  );
}

function formatDateTime(value) {
  if (!value) return '';

  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  }).format(new Date(value));
}

function Dashboard() {
  const [password, setPassword] = useState(() => sessionStorage.getItem('ibc-dashboard-password') || '');
  const [isUnlocked, setIsUnlocked] = useState(Boolean(password));
  const [bookings, setBookings] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedDashboardDay, setSelectedDashboardDay] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const filteredBookings = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return bookings.filter((booking) => {
      const matchesDay = selectedDashboardDay === 'all' || booking.start?.slice(0, 10) === selectedDashboardDay;
      const matchesQuery = !needle || `${booking.name} ${booking.company} ${booking.email} ${booking.phone} ${booking.summary}`
        .toLowerCase()
        .includes(needle);

      return matchesDay && matchesQuery;
    });
  }, [bookings, query, selectedDashboardDay]);
  const latestBooking = bookings[bookings.length - 1];

  async function loadBookings(currentPassword = password) {
    if (!currentPassword) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/bookings`, {
        headers: {
          'X-Dashboard-Password': currentPassword,
        },
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.message || 'Unable to load bookings.');
      }

      setBookings(payload.bookings || []);
      setIsUnlocked(true);
      sessionStorage.setItem('ibc-dashboard-password', currentPassword);
    } catch (dashboardError) {
      setError(dashboardError.message);
      setIsUnlocked(false);
      sessionStorage.removeItem('ibc-dashboard-password');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (password) {
      loadBookings(password);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function submitPassword(event) {
    event.preventDefault();
    loadBookings(password);
  }

  function logout() {
    sessionStorage.removeItem('ibc-dashboard-password');
    setPassword('');
    setIsUnlocked(false);
    setBookings([]);
    setError('');
  }

  return (
    <main className="dashboardShell">
      <header className="dashboardHeader">
        <div>
          <div className="brandMark dashboardMark" aria-label="Kineton">k<span /></div>
          <p className="eyebrow">Internal dashboard</p>
          <h1>IBC 2026 bookings</h1>
        </div>
        {isUnlocked && (
          <div className="dashboardActions">
            <button className="secondaryAction" disabled={loading} onClick={() => loadBookings()} type="button">
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button className="mutedButton" onClick={logout} type="button">Lock</button>
          </div>
        )}
      </header>

      {!isUnlocked && (
        <form className="dashboardLogin" onSubmit={submitPassword}>
          <label className="field wide">
            <span>Password</span>
            <input
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </label>
          {error && <p className="errorMessage" role="alert">{error}</p>}
          <button className="primaryAction" disabled={loading} type="submit">
            {loading ? 'Checking...' : 'Open dashboard'}
          </button>
        </form>
      )}

      {isUnlocked && (
        <section className="dashboardContent">
          <div className="metricGrid">
            <article className="metricCard">
              <span>Total bookings</span>
              <strong>{bookings.length}</strong>
            </article>
            <article className="metricCard">
              <span>Visible rows</span>
              <strong>{filteredBookings.length}</strong>
            </article>
            <article className="metricCard">
              <span>Latest booking</span>
              <strong>{latestBooking ? formatDateTime(latestBooking.created) : '-'}</strong>
            </article>
          </div>

          <div className="tableToolbar">
            <label className="field">
              <span>Search</span>
              <input
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Name, company, email, phone"
                type="search"
                value={query}
              />
            </label>
            <label className="field">
              <span>Day</span>
              <select
                onChange={(event) => setSelectedDashboardDay(event.target.value)}
                value={selectedDashboardDay}
              >
                {DASHBOARD_DAYS.map((day) => (
                  <option key={day.id} value={day.id}>{day.label}</option>
                ))}
              </select>
            </label>
          </div>

          {error && <p className="errorMessage" role="alert">{error}</p>}

          <div className="bookingTableWrap">
            <table className="bookingTable">
              <thead>
                <tr>
                  <th>Slot</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Calendar</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking) => (
                  <tr key={booking.id}>
                    <td>{formatDateTime(booking.start)}</td>
                    <td>{booking.name}</td>
                    <td>{booking.company}</td>
                    <td><a href={`mailto:${booking.email}`}>{booking.email}</a></td>
                    <td><a href={`tel:${booking.phone}`}>{booking.phone}</a></td>
                    <td>
                      {booking.htmlLink && (
                        <a href={booking.htmlLink} rel="noreferrer" target="_blank">Open</a>
                      )}
                    </td>
                  </tr>
                ))}
                {!filteredBookings.length && (
                  <tr>
                    <td colSpan="6">No bookings found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </main>
  );
}

function BookingApp() {
  const [selectedDayId, setSelectedDayId] = useState(DAYS[0].id);
  const [selectedTime, setSelectedTime] = useState('');
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ fullName: '', company: '', phone: '', email: '' });

  const selectedDay = useMemo(() => DAYS.find((day) => day.id === selectedDayId), [selectedDayId]);
  const slots = useMemo(() => makeSlots(selectedDay), [selectedDay]);

  function chooseDay(dayId) {
    setSelectedDayId(dayId);
    setSelectedTime('');
    setError('');
  }

  function updateForm(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function submitBooking(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, date: selectedDay.id, time: selectedTime }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.message || 'We could not confirm this slot. Please try another one.');
      }

      setStep(3);
    } catch (bookingError) {
      setError(bookingError.message);
    } finally {
      setSubmitting(false);
    }
  }

  function resetBooking() {
    setSelectedTime('');
    setForm({ fullName: '', company: '', phone: '', email: '' });
    setError('');
    setStep(1);
  }

  return (
    <main className="appShell">
      <BrandPanel />
      <section className="bookingPanel">
        <Stepper step={step} />

        {step === 1 && (
          <div className="bookingView">
            <header className="sectionHeader">
              <h2>Choose your day</h2>
              <p>All times are local to Amsterdam (CEST).</p>
            </header>

            <div className="dayGrid" role="list" aria-label="Available days">
              {DAYS.map((day) => (
                <button
                  className={day.id === selectedDay.id ? 'dayCard selected' : 'dayCard'}
                  key={day.id}
                  onClick={() => chooseDay(day.id)}
                  type="button"
                >
                  <span>{day.weekday}</span>
                  <strong>{day.day}</strong>
                  <small>{day.start}-{day.end}</small>
                </button>
              ))}
            </div>

            <div className="slotPanel">
              <div className="slotHeading">
                <h3>{selectedDay.label} — pick a time</h3>
                <span>{SLOT_MINUTES} minutes each</span>
              </div>

              <div className="slotGrid" aria-live="polite">
                {slots.map((time) => (
                  <button
                    className={selectedTime === time ? 'slotButton selected' : 'slotButton'}
                    key={time}
                    onClick={() => setSelectedTime(time)}
                    type="button"
                  >
                    {time}
                  </button>
                ))}
              </div>

              <p className="hint">Can't see a time that works? Write to us and we'll find one.</p>

              <button className="primaryAction compact" disabled={!selectedTime} onClick={() => setStep(2)} type="button">
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bookingView detailsView">
            <header className="sectionHeader">
              <h2>Nice — tell us who's coming</h2>
              <p>
                You picked <strong>{formatSlot(selectedDay, selectedTime)}</strong>.
                <button className="linkButton" onClick={() => setStep(1)} type="button">Change</button>
              </p>
            </header>

            <form className="detailsCard" onSubmit={submitBooking}>
              <label className="field wide">
                <span>Full name</span>
                <input autoComplete="name" name="fullName" onChange={updateForm} required value={form.fullName} />
              </label>
              <label className="field">
                <span>Company</span>
                <input autoComplete="organization" name="company" onChange={updateForm} required value={form.company} />
              </label>
              <label className="field">
                <span>Phone</span>
                <input autoComplete="tel" name="phone" onChange={updateForm} required type="tel" value={form.phone} />
              </label>
              <label className="field wide">
                <span>Work email</span>
                <input autoComplete="email" name="email" onChange={updateForm} required type="email" value={form.email} />
              </label>

              {error && <p className="errorMessage" role="alert">{error}</p>}

              <button className="primaryAction" disabled={submitting} type="submit">
                {submitting ? 'Confirming...' : 'Confirm my slot'}
              </button>
              <p className="privacyNote">We'll use your details only to organise this meeting and send the calendar invite.</p>
            </form>
          </div>
        )}

        {step === 3 && (
          <div className="bookingView doneView">
            <span className="successBadge" aria-hidden="true">✓</span>
            <h2>You're on the list, {form.fullName.split(' ')[0]}.</h2>
            <p>
              We've got you down for <strong>{formatSlot(selectedDay, selectedTime)}</strong> at {EVENT_LOCATION}.
              Add it to your calendar so it doesn't get lost in the show floor buzz.
            </p>

            <div className="summaryCard">
              <div className="summaryActions">
                <a className="primaryAction calendarLink" href={makeCalendarUrl(selectedDay, selectedTime, form)} rel="noreferrer" target="_blank">
                  Add to Google Calendar
                </a>
                <button className="secondaryAction" onClick={() => downloadIcs(selectedDay, selectedTime, form)} type="button">
                  Download .ics
                </button>
              </div>
              <dl>
                <div><dt>Name</dt><dd>{form.fullName}</dd></div>
                <div><dt>Company</dt><dd>{form.company}</dd></div>
                <div><dt>Email</dt><dd>{form.email}</dd></div>
                <div><dt>Phone</dt><dd>{form.phone}</dd></div>
              </dl>
            </div>

            <button className="mutedAction" onClick={resetBooking} type="button">Book another slot</button>
          </div>
        )}
      </section>
    </main>
  );
}

function App() {
  if (window.location.pathname.replace(/\/+$/, '') === '/dashboard') {
    return <Dashboard />;
  }

  return <BookingApp />;
}

export default App;
